<script>
/**
 * Creates a <section> with <h#> headline
 *
 * Default headline level is = 4
 *
 * If "h#" is set, the class will applied to the headline
 * If "cluster" is set, the class will be applied to the headline and all content will be indented
 * - additionally class "h5" will be set, if no other is defined
 * If no "h#" modifier is set, the default h3 will be set (if "cluster" is not set)
 *
 * If the section contains no children (other thant the generated headline) it will not be shown.
 */
import { mergeData } from 'vue-functional-data-merge';

export default {
  name: 'CustomSection',

  functional: true,

  props: {
    label: {
      type: [Number, String],
      required: false,
      default: null,
    },

    mandatory: {
      type: Boolean,
      required: false,
    },

    level: {
      type: [Number, String],
      required: false,
      default: 4,
    },

    cluster: {
      type: Boolean,
      required: false,
    },

    large: {
      type: Boolean,
      required: false,
    },

    group: {
      type: Boolean,
      required: false,
    },

    h1: {
      type: Boolean,
      required: false,
    },

    h2: {
      type: Boolean,
      required: false,
    },

    h3: {
      type: Boolean,
      required: false,
    },

    h4: {
      type: Boolean,
      required: false,
    },

    h5: {
      type: Boolean,
      required: false,
    },

    h6: {
      type: Boolean,
      required: false,
    },

    hlStyle: {
      type: String,
      required: false,
      default: '',
    },

    collapsed: {
      type: Boolean,
      required: false,
      // eslint-disable-next-line vue/no-boolean-default
      default: null,
    },
  },

  render(h, { props, data, parent, scopedSlots, listeners }) {
    let content = scopedSlots.default && scopedSlots.default();
    if (!content && !props.mandatory) return;

    const componentData = {
      staticClass: `s${props.level}`,
    };

    let staticClass = 'hl';

    if (props.cluster) {
      componentData.staticClass += ' cluster';
      staticClass += ' cluster';
    } else if (props.large) {
      staticClass += ' marketing-l';
    } else if (props.group) {
      staticClass += ' group';
    }

    let hNo;
    for (let i = 1; ; i++) {
      if (i === 7) {
        // no h# found!
        if (props.cluster) {
          hNo = 'h5';
        } else {
          hNo = 'h3';
        }
        break;
      }
      const key = `h${i}`;
      if (props[key]) {
        hNo = key;
        break;
      }
    }
    staticClass += ' ' + hNo;

    if (!props.mandatory && props.collapsed === null) {
      staticClass += ' optional';
    }

    let headline;
    if (scopedSlots.label) {
      headline = h(`h${props.level}`, { staticClass, style: props.hlStyle }, [scopedSlots.label()]);
    } else if (props.label) {
      headline = h(`h${props.level}`, { staticClass, style: props.hlStyle, domProps: { innerHTML: parent.$to(props.label) } });
    }

    if (props.collapsed !== null) {
      const VCollapsable = require('@/components/Collapsable.vue').default;
      headline = h(
        VCollapsable,
        {
          attrs: { class: hNo, collapsed: props.collapsed },
          on: {
            // .sync on functional components is quite tricky. This solution is inspired from: https://github.com/bootstrap-vue/bootstrap-vue/blob/dev/src/components/button/button.js
            'update:collapsed'() {
              // Send `.sync` updates to any "collapsed" prop (if `.sync` listeners)
              // `concat()` will normalize the value to an array without double wrapping an array value in an array
              if (listeners && listeners['update:collapsed']) {
                Array.prototype.concat(listeners['update:collapsed']).forEach(fn => {
                  if (typeof fn === 'function') {
                    fn(!props.collapsed);
                  }
                });
              }
            },
          },
        },
        [headline]
      );
      if (props.collapsed) {
        content = null;
      }
    }

    return h('section', mergeData(data, componentData), [headline, content]);
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.hl.marketing-l {
  padding-top: 48px;
}

.hl.group {
  padding-bottom: 6px;
  font-size: 21px;
  font-weight: 300;
  color: $color-gray;
}

.hl.optional:only-child {
  display: none;
}

section.cluster {
  padding: 24px 12px 12px;

  h1.hl,
  h2.hl,
  h3.hl,
  h4.hl,
  h5.hl,
  h6.hl {
    padding-top: 0;
    margin: 0 -12px 12px;
  }
}
</style>
